import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useResponsive } from '../../hooks';
import { spacing } from '../../utils/designTokens';
import { HomeIcon, ArchiveBoxIcon, UserCircleIcon, WrenchScrewdriverIcon, DocumentChartBarIcon, ArrowsRightLeftIcon, Squares2X2Icon } from '@heroicons/react/24/outline'; // Added Squares2X2Icon

export const NavigationBar: React.FC = () => {
  const { isSmallerThan } = useResponsive();
  const isMobile = isSmallerThan('md');
  const location = useLocation();
  const currentPath = location.pathname;
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: Squares2X2Icon },
    { name: 'Envanter', path: '/inventory', icon: ArchiveBoxIcon },
    { name: 'Tamiratlar', path: '/repairs', icon: WrenchScrewdriverIcon },
    { name: 'Satışlar', path: '/transactions', icon: ArrowsRightLeftIcon },
    { name: 'Raporlar', path: '/reports', icon: DocumentChartBarIcon },
    { name: 'Profil', path: '/profile', icon: UserCircleIcon },
  ];

  // Desktop Sidebar
  const DesktopSidebar = () => (
    <aside className="fixed left-0 top-0 bottom-0 w-[90px] bg-primary-charcoal text-accent-white flex flex-col justify-between py-5 z-10">
      <div className="flex flex-col items-center space-y-4 pt-5">
        {/* Dashboard Button */}
        <Link
          to="/dashboard"
          className={`w-16 h-20 rounded-lg flex flex-col items-center justify-center transition-colors ${
            currentPath === '/dashboard' ? 'bg-primary-silver text-primary-charcoal' : 'hover:bg-primary-charcoal/80'
          }`}
          aria-label="Dashboard"
          onMouseEnter={() => setHoveredItem('dashboard')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <Squares2X2Icon className="w-6 h-6" />
          <span className="text-[10px] font-medium mt-1">Dashboard</span>
        </Link>

        {/* Inventory Button */}
        <Link 
          to="/inventory"
          className={`w-16 h-20 rounded-lg flex flex-col items-center justify-center transition-colors ${
            currentPath === '/inventory' ? 'bg-primary-silver text-primary-charcoal' : 'hover:bg-primary-charcoal/80'
          }`}
          aria-label="Envanter"
          onMouseEnter={() => setHoveredItem('inventory')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
          </svg>
          <span className="text-[10px] font-medium mt-1">Envanter</span>
        </Link>

        {/* Repairs Button */}
        <Link 
          to="/repairs"
          className={`w-16 h-20 rounded-lg flex flex-col items-center justify-center transition-colors ${
            currentPath === '/repairs' ? 'bg-primary-silver text-primary-charcoal' : 'hover:bg-primary-charcoal/80'
          }`}
          aria-label="Tamiratlar"
          onMouseEnter={() => setHoveredItem('repairs')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008Z"></path>
          </svg>
          <span className="text-[10px] font-medium mt-1">Tamiratlar</span>
        </Link>

        {/* Transactions Button - Desktop */}
        <Link
          to="/transactions"
          className={`w-16 h-20 rounded-lg flex flex-col items-center justify-center transition-colors ${
            currentPath === '/transactions' || currentPath.startsWith('/transactions/') ? 'bg-primary-silver text-primary-charcoal' : 'hover:bg-primary-charcoal/80'
          }`}
          aria-label="İşlemler"
          onMouseEnter={() => setHoveredItem('transactions')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <span className="text-[10px] font-medium mt-1">Satışlar</span>
        </Link>

        {/* Reports Button - Desktop */}
        <Link
          to="/reports"
          className={`w-16 h-20 rounded-lg flex flex-col items-center justify-center transition-colors ${
            currentPath === '/reports' || currentPath.startsWith('/reports/') ? 'bg-primary-silver text-primary-charcoal' : 'hover:bg-primary-charcoal/80'
          }`}
          aria-label="Raporlar"
          onMouseEnter={() => setHoveredItem('reports')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
          <span className="text-[10px] font-medium mt-1">Raporlar</span>
        </Link>
      </div>

      <div className="flex flex-col items-center space-y-2">
        {/* User Profile Button */}
        <Link 
          to="/profile"
          className={`w-16 h-20 rounded-lg flex flex-col items-center justify-center transition-colors ${
            currentPath === '/profile' ? 'bg-primary-silver text-primary-charcoal' : 'hover:bg-primary-charcoal/80'
          }`}
          aria-label="Profil"
          onMouseEnter={() => setHoveredItem('profile')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="text-[10px] font-medium mt-1">Profil</span>
        </Link>
      </div>
    </aside>
  );

  // Mobile Bottom Nav
  const MobileBottomNav = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-accent-white border-t border-primary-silver/30 flex items-center justify-around px-3 py-2 z-10">
      {/* Dashboard Button */}
      <Link
        to="/dashboard"
        className={`w-16 h-16 flex flex-col items-center justify-center gap-1 ${
          currentPath === '/dashboard'
            ? 'text-primary-charcoal bg-primary-silver/20 rounded-full'
            : 'text-primary-charcoal/60 hover:bg-primary-silver/20'
        } ${spacing.mobile.touchTarget}`}
        aria-label="Dashboard"
      >
        <Squares2X2Icon className="w-6 h-6" />
        <span className="text-xs font-medium">Dashboard</span>
      </Link>

      {/* Inventory Button */}
      <Link 
        to="/inventory"
        className={`w-16 h-16 flex flex-col items-center justify-center gap-1 ${
          currentPath === '/inventory' 
            ? 'text-primary-charcoal bg-primary-silver/20 rounded-full' 
            : 'text-primary-charcoal/60 hover:bg-primary-silver/20'
        } ${spacing.mobile.touchTarget}`}
        aria-label="Envanter"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
        </svg>
        <span className="text-xs font-medium">Envanter</span>
      </Link>

      {/* Transactions Button - Mobile */}
      <Link
        to="/transactions"
        className={`w-16 h-16 flex flex-col items-center justify-center gap-1 ${
          currentPath === '/transactions' || currentPath.startsWith('/transactions/')
            ? 'text-primary-charcoal bg-primary-silver/20 rounded-full'
            : 'text-primary-charcoal/60 hover:bg-primary-silver/20'
        } ${spacing.mobile.touchTarget}`}
        aria-label="İşlemler"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
        <span className="text-xs font-medium">İşlemler</span>
      </Link>

      {/* Reports Button - Mobile */}
      <Link
        to="/reports"
        className={`w-16 h-16 flex flex-col items-center justify-center gap-1 ${
          currentPath === '/reports' || currentPath.startsWith('/reports/')
            ? 'text-primary-charcoal bg-primary-silver/20 rounded-full'
            : 'text-primary-charcoal/60 hover:bg-primary-silver/20'
        } ${spacing.mobile.touchTarget}`}
        aria-label="Raporlar"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
        <span className="text-xs font-medium">Raporlar</span>
      </Link>

      {/* Repairs Button */}
      <Link 
        to="/repairs"
        className={`w-16 h-16 flex flex-col items-center justify-center gap-1 ${
          currentPath === '/repairs' 
            ? 'text-primary-charcoal bg-primary-silver/20 rounded-full' 
            : 'text-primary-charcoal/60 hover:bg-primary-silver/20'
        } ${spacing.mobile.touchTarget}`}
        aria-label="Tamiratlar"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008Z"></path>
        </svg>
        <span className="text-xs font-medium">Tamiratlar</span>
      </Link>

      {/* Profile Button */}
      <Link 
        to="/profile"
        className={`w-16 h-16 flex flex-col items-center justify-center gap-1 ${
          currentPath === '/profile' 
            ? 'text-primary-charcoal bg-primary-silver/20 rounded-full' 
            : 'text-primary-charcoal/60 hover:bg-primary-silver/20'
        } ${spacing.mobile.touchTarget}`}
        aria-label="Profil"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <span className="text-xs font-medium">Profil</span>
      </Link>
    </div>
  );

  return isMobile ? <MobileBottomNav /> : <DesktopSidebar />;
};

export default NavigationBar; 